import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { aboutImage } from "@/mocks/heroes";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/dbb6vnp1375vv1d5bo60";

const scopeItems = [
  {
    icon: "ri-database-2-line",
    title: "影片基础资料",
    desc: "年份、地区、片长、类型、导演与主要演员，按统一字段整理，便于横向比较与筛选。",
  },
  {
    icon: "ri-file-text-line",
    title: "分段剧情介绍",
    desc: "每部影片提供三段式剧情梗概，讲清楚人物关系与叙事走向，不剧透关键反转。",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "主题标签与片单",
    desc: "用「雨夜」「旧同学」「家庭伦理」等标签做二次归类，方便按情绪与主题找片。",
  },
  {
    icon: "ri-bookmark-line",
    title: "本地收藏",
    desc: "收藏只保存在你的浏览器里，不上传、不追踪，换设备后不会同步。",
  },
];

const disclaimers = [
  "本站为影视资料整理与介绍类网站，仅面向 18 周岁以上的成年用户。未成年人请在监护人陪同下离开本站。",
  "本站不提供任何影片的在线播放、下载、云盘分享或付费购买入口，页面中出现的播放/下载类字样均不属于本站功能。",
  "站内所有影片名称、导演、演员、评分与剧情内容均为产品演示所用的虚构数据，与任何真实作品、真实人物无关。",
  "如本站内容不慎涉及版权或信息错误，请通过下方留言或邮箱与我们联系，我们会在核实后及时处理。",
  "用户应遵守所在地法律法规，因个人使用本站信息产生的一切后果由用户自行承担。",
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);
  const [statusText, setStatusText] = useState("");

  useSeo({
    title: "关于我们 - 日韩伦理电影资料站",
    description:
      "了解日韩伦理电影资料站的定位、内容范围与免责声明。本站仅整理影片资料与剧情介绍，不提供播放与下载，并提供留言联系方式。",
    keywords: "关于日韩伦理电影,网站介绍,免责声明,联系方式,影视资料站",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);

    const honey = String(formData.get("website_alt") || "").trim();
    if (honey) {
      setStatus("success");
      setStatusText("留言已提交，感谢你的反馈。");
      formElement.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");
    setStatusText("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试。";
      const isSpam = String(serverMessage).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMessage);
        return;
      }

      setStatus("success");
      setStatusText("留言已提交，感谢你的反馈。");
      formElement.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setFormError("网络异常，留言没有发送成功，请稍后重试。");
    }
  };

  return (
    <div className="bg-background-50">
      <section className="relative overflow-hidden border-b border-background-200/70">
        <img
          src={aboutImage}
          alt="夜澜影片资料档案室"
          title="夜澜 · 日韩伦理电影资料站"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/35"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/30"></div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 md:py-24">
          <nav aria-label="面包屑" className="mb-7 flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="text-primary-300">关于我们</span>
          </nav>

          <p className="mb-3 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.3em] text-primary-300">
            <span className="h-px w-8 bg-primary-500/80"></span>
            About yelan
          </p>
          <h1 className="max-w-[720px] font-heading text-3xl font-semibold leading-[1.25] text-foreground-950 md:text-[46px]">
            把影片讲清楚，
            <br className="hidden sm:block" />
            把选择留给你
          </h1>
          <p className="mt-5 max-w-[560px] text-[14px] leading-[1.9] text-foreground-600">
            夜澜是一个专注日韩伦理题材的电影资料站。我们不生产内容，也不做资源聚合，
            只把每部作品的班底、类型与剧情整理成可以被快速阅读的结构化资料。
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/category/伦理"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-500"
            >
              浏览伦理分类
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="#contact-form"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-3 text-[13px] text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-200"
            >
              <i className="ri-mail-line"></i>
              给我们留言
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">站点定位与内容范围</h2>
            <p className="mt-4 text-[13.5px] leading-[2] text-foreground-600">
              日韩伦理题材作品往往以家庭、婚姻与亲密关系为切口，讨论人在道德边界上的犹疑。
              这类作品的信息分散在各类影评与条目里，缺少统一字段的整理。夜澜想做的，是把这些资料集中起来，
              用一致的格式呈现：年份、地区、导演、主演、类型、片长、评分，以及一段不剧透的剧情梗概。
            </p>
            <p className="mt-3 text-[13.5px] leading-[2] text-foreground-600">
              目前站内按伦理、剧情、情色、经典四个方向归类，覆盖 2021 至 2025 年的日韩作品。
              你可以按地区、年份与评分区间筛选，也可以直接搜索片名或导演。收藏功能仅作用于本地浏览器。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {scopeItems.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200/80 bg-background-100 p-4">
                  <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-accent-900/60 text-accent-200">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                  <h3 className="text-[13.5px] font-semibold text-foreground-800">{item.title}</h3>
                  <p className="mt-1.5 text-[12px] leading-[1.85] text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-primary-900/60 bg-primary-950/30 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-shield-check-line text-primary-300"></i>
                免责声明
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {disclaimers.map((item) => (
                  <li key={item} className="flex gap-2.5 text-[12px] leading-[1.85] text-foreground-600">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[5px] text-primary-400"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
                <i className="ri-contacts-line text-secondary-300"></i>
                联系方式
              </h2>
              <p className="mt-3 text-[12px] leading-relaxed text-foreground-500">
                内容纠错、版权申诉或合作沟通，建议优先使用邮箱。工作日一般在 48 小时内回复。
              </p>
              <a
                href="mailto:hello@yelan-films.example"
                className="mt-3 inline-flex items-center gap-2 rounded-md border border-background-300/70 px-3 py-2 font-label text-[12px] text-secondary-300 transition-colors hover:border-secondary-500"
              >
                <i className="ri-mail-send-line"></i>
                hello@yelan-films.example
              </a>
            </div>
          </div>
        </div>

        <section id="contact-form" className="mt-14 scroll-mt-24 border-t border-background-200/70 pt-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-primary-300">
                <span className="h-px w-6 bg-primary-500/70"></span>
                Contact
              </p>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">站内留言</h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-500">
                发现资料错误、想补充影片，或者对分类有更好的建议，都可以在这里告诉我们。
                留言会直接发送到站务后台，我们不会公开你的邮箱。
              </p>
              <div className="mt-5 rounded-md border border-background-200/80 bg-background-100 p-4">
                <p className="font-label text-[10.5px] uppercase tracking-[0.16em] text-foreground-500">Other channels</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={`/category/${category.key}`}
                      className="rounded-md border border-background-300/70 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                    >
                      {category.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-6">
                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label htmlFor="contact-name" className="mb-1.5 block text-[12px] text-foreground-600">
                        你的称呼
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="怎么称呼你"
                        className="box-border h-11 w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 text-[13px] text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <label htmlFor="contact-email" className="mb-1.5 block text-[12px] text-foreground-600">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="用于回复你的邮箱"
                        className="box-border h-11 w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 text-[13px] text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="contact-message" className="block text-[12px] text-foreground-600">
                        留言内容
                      </label>
                      <span className="font-label text-[10.5px] text-foreground-500">{messageLength} / 500</span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="想说的话写在这里，最多 500 字。"
                      className="box-border w-full resize-y rounded-md border border-background-300/70 bg-background-50 px-3.5 py-3 text-[13px] leading-relaxed text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
                    ></textarea>
                  </div>

                  <div className="min-w-0">
                    <label htmlFor="contact-website-alt" className="field-hidden" aria-hidden="true">
                      Alternate website
                    </label>
                    <input
                      id="contact-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="field-hidden"
                    />
                  </div>

                  {status === "success" && (
                    <div className="flex items-start gap-2 rounded-md border border-secondary-700/50 bg-secondary-950/40 px-3.5 py-3 text-[12px] leading-relaxed text-secondary-200">
                      <i className="ri-checkbox-circle-line mt-0.5"></i>
                      <span>{statusText}</span>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="flex items-start gap-2 rounded-md border border-primary-700/60 bg-primary-950/50 px-3.5 py-3 text-[12px] leading-relaxed text-primary-200">
                      <i className="ri-error-warning-line mt-0.5"></i>
                      <span>{formError}</span>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-foreground-500">
                      提交即表示你已阅读并同意上方的免责声明。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          提交留言
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}